import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  addMonths,
  endOfMonth,
  format,
  getDay,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns';
import { CalendarRange, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;

type TabId = 'between' | 'last' | 'since';

function parseIsoDay(value: string): Date | null {
  if (!value || value.length < 10) return null;
  const d = parseISO(value.slice(0, 10));
  return Number.isNaN(d.getTime()) ? null : startOfDay(d);
}

function toIso(d: Date): string {
  return format(startOfDay(d), 'yyyy-MM-dd');
}

function buildMonthGrid(viewMonth: Date): Date[] {
  const start = startOfMonth(viewMonth);
  const end = endOfMonth(viewMonth);
  const startDate = new Date(start);
  startDate.setDate(startDate.getDate() - getDay(start));
  const endDate = new Date(end);
  endDate.setDate(endDate.getDate() + (6 - getDay(end)));
  const days: Date[] = [];
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }
  return days;
}

function fmtDisplay(iso: string): string {
  const p = parseIsoDay(iso);
  return p ? format(p, 'MMM d, yyyy') : '';
}

export type EpcScheduleDateRangePickerProps = {
  dateFrom: string;
  dateTo: string;
  onDatesChange: (from: string, to: string) => void;
  accentHex?: string;
  /** Optional label id for aria */
  labelId?: string;
  /** Stable DOM id for the visible trigger button */
  triggerDomId?: string;
  /** Hide the default “Select date range” button; position popover from `anchorRef` */
  hideTrigger?: boolean;
  /** Element to anchor the popover (e.g. “Start to end” button) when `hideTrigger` */
  anchorRef?: RefObject<HTMLElement | null>;
  /** Controlled open state (use with `onOpenChange`) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const PRESETS: { id: string; label: string }[] = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'today', label: 'Today' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last14', label: 'Last 14 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'last60', label: 'Last 60 days' },
  { id: 'last90', label: 'Last 90 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'all', label: 'All time' },
];

function applyPreset(id: string): { from: string; to: string } {
  const today = startOfDay(new Date());
  switch (id) {
    case 'yesterday': {
      const d = subDays(today, 1);
      return { from: toIso(d), to: toIso(d) };
    }
    case 'today':
      return { from: toIso(today), to: toIso(today) };
    case 'last7':
      return { from: toIso(subDays(today, 6)), to: toIso(today) };
    case 'last14':
      return { from: toIso(subDays(today, 13)), to: toIso(today) };
    case 'last30':
      return { from: toIso(subDays(today, 29)), to: toIso(today) };
    case 'last60':
      return { from: toIso(subDays(today, 59)), to: toIso(today) };
    case 'last90':
      return { from: toIso(subDays(today, 89)), to: toIso(today) };
    case 'thisMonth': {
      const start = startOfMonth(today);
      const end = endOfMonth(today);
      const endCap = isAfter(end, today) ? today : end;
      return { from: toIso(start), to: toIso(endCap) };
    }
    case 'all':
    default:
      return { from: '', to: '' };
  }
}

export function EpcScheduleDateRangePicker({
  dateFrom,
  dateTo,
  onDatesChange,
  accentHex = '#0d9488',
  labelId,
  triggerDomId,
  hideTrigger = false,
  anchorRef,
  open: openControlled,
  onOpenChange,
}: EpcScheduleDateRangePickerProps) {
  const autoId = useId();
  const triggerId = triggerDomId ?? `${autoId}-trigger`;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openControlled !== undefined;
  const open = isControlled ? openControlled : uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      onOpenChange?.(next);
      if (!isControlled) setUncontrolledOpen(next);
    },
    [isControlled, onOpenChange],
  );
  const [tab, setTab] = useState<TabId>('between');
  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);
  const [lastDays, setLastDays] = useState(7);
  const [viewMonth, setViewMonth] = useState(() => parseIsoDay(dateFrom) ?? parseIsoDay(dateTo) ?? new Date());
  const triggerWrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverBox, setPopoverBox] = useState({ top: 0, left: 0, width: 704 });

  const syncDraftFromProps = useCallback(() => {
    setDraftFrom(dateFrom);
    setDraftTo(dateTo);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (open) syncDraftFromProps();
  }, [open, syncDraftFromProps]);

  useEffect(() => {
    const p = parseIsoDay(draftFrom) ?? parseIsoDay(draftTo);
    if (p) setViewMonth(p);
  }, [draftFrom, draftTo, open]);

  const updatePopoverPosition = useCallback(() => {
    const el = hideTrigger ? anchorRef?.current : triggerWrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 12;
    const maxW = Math.min(704, window.innerWidth - margin * 2);
    let left = r.left;
    if (left + maxW > window.innerWidth - margin) left = window.innerWidth - margin - maxW;
    if (left < margin) left = margin;
    const top = r.bottom + 8;
    const maxTop = window.innerHeight - margin - 120;
    const safeTop = top > maxTop ? Math.max(margin, r.top - 8 - 400) : top;
    setPopoverBox({ top: safeTop, left, width: maxW });
  }, [hideTrigger, anchorRef]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePopoverPosition();
  }, [open, tab, updatePopoverPosition, hideTrigger, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const node = e.target as Node;
      if (triggerWrapRef.current?.contains(node)) return;
      if (hideTrigger && anchorRef?.current?.contains(node)) return;
      if (popoverRef.current?.contains(node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onResize = () => updatePopoverPosition();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, updatePopoverPosition, hideTrigger, anchorRef, setOpen]);

  const summary = useMemo(() => {
    if (!dateFrom && !dateTo) return 'Select date range';
    if (dateFrom && dateTo) return `${fmtDisplay(dateFrom)} – ${fmtDisplay(dateTo)}`;
    if (dateFrom) return `From ${fmtDisplay(dateFrom)}`;
    return `Until ${fmtDisplay(dateTo)}`;
  }, [dateFrom, dateTo]);

  const handlePreset = (id: string) => {
    const { from, to } = applyPreset(id);
    setDraftFrom(from);
    setDraftTo(to);
    setTab('between');
    onDatesChange(from, to);
    setOpen(false);
  };

  const handleApply = () => {
    if (tab === 'last') {
      const today = startOfDay(new Date());
      const n = Math.max(1, Math.min(3650, Math.floor(lastDays)));
      const from = subDays(today, n - 1);
      onDatesChange(toIso(from), toIso(today));
      setDraftFrom(toIso(from));
      setDraftTo(toIso(today));
    } else if (tab === 'since') {
      onDatesChange(draftFrom, '');
    } else {
      let a = draftFrom;
      let b = draftTo;
      if (a && b) {
        const da = parseIsoDay(a)!;
        const db = parseIsoDay(b)!;
        if (isAfter(da, db)) {
          const t = a;
          a = b;
          b = t;
        }
      }
      onDatesChange(a, b);
    }
    setOpen(false);
  };

  const handleCancel = () => {
    syncDraftFromProps();
    setOpen(false);
  };

  const pickRangeDay = (d: Date) => {
    const iso = toIso(d);
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(iso);
      setDraftTo('');
      return;
    }
    const start = parseIsoDay(draftFrom)!;
    if (isBefore(d, start)) {
      setDraftTo(draftFrom);
      setDraftFrom(iso);
    } else {
      setDraftTo(iso);
    }
  };

  const pickSinceDay = (d: Date) => {
    setDraftFrom(toIso(d));
    setDraftTo('');
  };

  const gridLeft = buildMonthGrid(viewMonth);
  const gridRight = buildMonthGrid(addMonths(viewMonth, 1));
  const today = startOfDay(new Date());

  const rangeStart = parseIsoDay(draftFrom);
  const rangeEnd = parseIsoDay(draftTo);

  const dayCellClass = (d: Date, view: Date, mode: 'range' | 'since') => {
    const inMonth = isSameMonth(d, view);
    const disabled = isAfter(d, today);
    const rs = rangeStart;
    let inRange = false;
    let isEndpoint = false;
    if (mode === 'range' && rs) {
      const re = draftTo && rangeEnd ? rangeEnd : rs;
      inRange = !isBefore(d, rs) && !isAfter(d, re);
      isEndpoint =
        isSameDay(d, rs) || (draftTo && rangeEnd ? isSameDay(d, rangeEnd) : isSameDay(d, rs));
    } else if (mode === 'since' && rs) {
      inRange = !isBefore(d, rs) && !isAfter(d, today);
      isEndpoint = isSameDay(d, rs);
    }

    return twMerge(
      'relative flex h-9 items-center justify-center rounded-lg text-[11px] font-semibold tabular-nums transition',
      !inMonth && 'text-slate-300',
      inMonth && !disabled && 'text-slate-800',
      disabled && 'cursor-not-allowed opacity-30',
      inRange && inMonth && !isEndpoint && 'bg-teal-100/90 text-slate-800 dark:bg-teal-900/30',
      mode === 'since' && inRange && inMonth && !isEndpoint && 'bg-slate-100/80',
    );
  };

  const endCircleStyle = (d: Date, view: Date, mode: 'range' | 'since'): CSSProperties | undefined => {
    const inMonth = isSameMonth(d, view);
    const rs = rangeStart;
    if (!rs || !inMonth) return undefined;
    if (mode === 'since') {
      if (!isSameDay(d, rs)) return undefined;
    } else {
      const re = draftTo && rangeEnd ? rangeEnd : rs;
      const isEdge = isSameDay(d, rs) || (draftTo && rangeEnd ? isSameDay(d, rangeEnd) : isSameDay(d, rs));
      if (!isEdge) return undefined;
    }
    return {
      background: `linear-gradient(145deg, ${accentHex}, ${accentHex}dd)`,
      color: '#fff',
      boxShadow: '0 2px 8px rgba(13, 148, 136, 0.35)',
    };
  };

  const renderMonth = (
    grid: Date[],
    view: Date,
    mode: 'range' | 'since',
    onPick: (d: Date) => void,
    showMonthTitle = true,
  ) => (
    <div className="min-w-0 flex-1">
      {showMonthTitle ? (
        <p className="mb-2 text-center text-xs font-bold tabular-nums text-slate-700">{format(view, 'MMMM yyyy')}</p>
      ) : null}
      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS.map((wd) => (
          <span key={wd} className="py-1 text-[9px] font-bold uppercase tracking-wider text-slate-400">
            {wd}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {grid.map((d) => {
          const inMonth = isSameMonth(d, view);
          const disabled = isAfter(d, today);
          const style = endCircleStyle(d, view, mode);
          return (
            <button
              key={`${view.toISOString()}-${d.toISOString()}`}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onPick(d)}
              className={dayCellClass(d, view, mode)}
              style={style}
            >
              {format(d, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );

  const popover = (
    <AnimatePresence>
      {open ? (
        <motion.div
          ref={popoverRef}
          role="dialog"
          aria-label="Date range"
          initial={{ opacity: 0, y: -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.99 }}
          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
          style={{
            position: 'fixed',
            top: popoverBox.top,
            left: popoverBox.left,
            width: popoverBox.width,
            zIndex: 500,
          }}
          className="max-h-[min(90vh,560px)] overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-slate-900/[0.04]"
        >
            <div className="flex max-h-[min(90vh,560px)] flex-col sm:flex-row">
              {/* Presets */}
              <div className="shrink-0 border-b border-slate-100 bg-slate-50/95 sm:w-44 sm:border-b-0 sm:border-r">
                <p className="border-b border-slate-100 px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Quick ranges
                </p>
                <nav className="max-h-40 overflow-y-auto p-1.5 sm:max-h-none">
                  {PRESETS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handlePreset(p.id)}
                      className="flex w-full rounded-lg px-2.5 py-2 text-left text-xs font-medium text-slate-700 transition hover:bg-white hover:shadow-sm"
                    >
                      {p.label}
                    </button>
                  ))}
                </nav>
                <button
                  type="button"
                  onClick={() => setTab('between')}
                  className={twMerge(
                    'm-1.5 mt-0 w-[calc(100%-12px)] rounded-lg px-2.5 py-2 text-left text-xs font-semibold transition',
                    tab === 'between'
                      ? 'bg-white text-blue-600 shadow-sm ring-1 ring-slate-200/80'
                      : 'text-slate-600 hover:bg-white/80',
                  )}
                >
                  Custom range
                </button>
              </div>

              {/* Main */}
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex gap-1 border-b border-slate-100 px-2 pt-2">
                  {(
                    [
                      { id: 'between' as const, label: 'In between' },
                      { id: 'last' as const, label: 'In the last' },
                      { id: 'since' as const, label: 'Since' },
                    ] as const
                  ).map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTab(t.id)}
                      className={twMerge(
                        'relative flex-1 px-2 py-2.5 text-center text-xs font-semibold transition',
                        tab === t.id ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700',
                      )}
                    >
                      {t.label}
                      {tab === t.id ? (
                        <span
                          className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                          style={{ background: accentHex }}
                        />
                      ) : null}
                    </button>
                  ))}
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-3">
                  {tab === 'between' && (
                    <>
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <input
                          type="date"
                          value={draftFrom.slice(0, 10)}
                          max={format(today, 'yyyy-MM-dd')}
                          onChange={(e) => setDraftFrom(e.target.value)}
                          className="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 px-2 text-xs font-semibold tabular-nums text-slate-900 shadow-sm focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                        />
                        <span className="text-xs font-medium text-slate-400">and</span>
                        <input
                          type="date"
                          value={draftTo.slice(0, 10)}
                          max={format(today, 'yyyy-MM-dd')}
                          onChange={(e) => setDraftTo(e.target.value)}
                          className="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 px-2 text-xs font-semibold tabular-nums text-slate-900 shadow-sm focus:border-teal-400 focus:outline-none focus:ring-teal-500/20"
                        />
                      </div>
                      <div className="mb-3 flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
                        <button
                          type="button"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100"
                          aria-label="Previous months"
                          onClick={() => setViewMonth((m) => subMonths(m, 1))}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <p className="min-w-0 flex-1 text-center text-[11px] font-bold tabular-nums text-slate-700 sm:text-xs">
                          {format(viewMonth, 'MMMM yyyy')} · {format(addMonths(viewMonth, 1), 'MMMM yyyy')}
                        </p>
                        <button
                          type="button"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100"
                          aria-label="Next months"
                          onClick={() => setViewMonth((m) => addMonths(m, 1))}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex flex-col gap-4 sm:flex-row">
                        {renderMonth(gridLeft, viewMonth, 'range', pickRangeDay, false)}
                        {renderMonth(gridRight, addMonths(viewMonth, 1), 'range', pickRangeDay, false)}
                      </div>
                    </>
                  )}

                  {tab === 'last' && (
                    <div className="flex flex-col gap-4 py-2">
                      <p className="text-xs text-slate-600">
                        Include tasks from the last <strong>N</strong> calendar days through today.
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        {[7, 14, 30, 60, 90].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setLastDays(n)}
                            className={twMerge(
                              'rounded-full px-3 py-1.5 text-xs font-semibold transition',
                              lastDays === n
                                ? 'text-white shadow-sm'
                                : 'border border-slate-200 bg-white text-slate-700 hover:border-teal-300',
                            )}
                            style={
                              lastDays === n
                                ? { background: accentHex }
                                : undefined
                            }
                          >
                            {n} days
                          </button>
                        ))}
                      </div>
                      <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                        <span className="w-24 shrink-0">Days</span>
                        <input
                          type="number"
                          min={1}
                          max={3650}
                          value={lastDays}
                          onChange={(e) => setLastDays(Number(e.target.value) || 1)}
                          className="h-10 w-28 rounded-lg border border-slate-200 px-2 text-sm font-semibold tabular-nums shadow-sm focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                        />
                      </label>
                    </div>
                  )}

                  {tab === 'since' && (
                    <>
                      <p className="mb-2 text-xs text-slate-600">Tasks on or after the selected day (no end date).</p>
                      <div className="mb-2 flex items-center justify-between">
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                          aria-label="Previous month"
                          onClick={() => setViewMonth((m) => subMonths(m, 1))}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                          aria-label="Next month"
                          onClick={() => setViewMonth((m) => addMonths(m, 1))}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mx-auto max-w-[18rem]">{renderMonth(gridLeft, viewMonth, 'since', pickSinceDay)}</div>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/80 px-3 py-2.5">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="rounded-lg px-4 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-200/80"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    className="rounded-lg px-5 py-2 text-xs font-semibold text-white shadow-md transition hover:opacity-95"
                    style={{ background: accentHex }}
                  >
                    Apply range
                  </button>
                </div>
              </div>
            </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <>
      {!hideTrigger ? (
        <div ref={triggerWrapRef} className="relative z-30 min-w-0">
          <button
            id={triggerId}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-labelledby={labelId}
            onClick={() => setOpen(!open)}
            className="flex h-11 w-full min-w-0 items-center gap-2 rounded-xl border border-slate-200/90 bg-white py-2 pl-3 pr-3 text-left text-sm font-semibold tabular-nums text-slate-900 shadow-sm ring-1 ring-slate-900/[0.02] transition hover:border-teal-300/80 focus:border-teal-400/80 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
          >
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white shadow-sm"
              style={{ background: `linear-gradient(145deg, ${accentHex}, ${accentHex}cc)` }}
              aria-hidden
            >
              <CalendarRange className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <span className="min-w-0 flex-1 truncate text-slate-800">{summary}</span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      ) : null}
      {typeof document !== 'undefined' ? createPortal(popover, document.body) : null}
    </>
  );
}
